import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from '..';
import { outreachApi } from '../../api/outreach.api';
import {
  ICreateOutreachResult,
  IOutreach,
  IOutreachDetail,
  IOutreachPagination,
} from '../../types/outreach.types';

interface IOutreachState {
  loading: boolean;
  outreaches: IOutreach[];
  selectedOutreach: IOutreachDetail | null;
  createResult: ICreateOutreachResult | null;
  pagination: IOutreachPagination | null;
}

const initialState: IOutreachState = {
  loading: false,
  outreaches: [],
  selectedOutreach: null,
  createResult: null,
  pagination: null,
};

export const outreachSlice = createSlice({
  name: 'outreach',
  initialState,
  reducers: {
    clearCreateResult: (state) => {
      state.createResult = null;
    },
    clearSelectedOutreach: (state) => {
      state.selectedOutreach = null;
    },
  },
  extraReducers: (builder) => {
    // Upload outreach
    builder.addCase(uploadOutreach.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(uploadOutreach.fulfilled, (state, { payload }: any) => {
      state.loading = false;
      state.createResult = payload?.data || payload;
      toast.success('Campaign uploaded successfully');
    });
    builder.addCase(uploadOutreach.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to upload campaign');
    });

    // Send outreach
    builder.addCase(sendOutreach.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendOutreach.fulfilled, (state) => {
      state.loading = false;
      state.createResult = null;
      toast.success('Campaign sending started');
    });
    builder.addCase(sendOutreach.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to send campaign');
    });

    // Fetch outreaches
    builder.addCase(fetchOutreaches.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOutreaches.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.outreaches = payload.data || [];
      state.pagination = payload.pagination || null;
    });
    builder.addCase(fetchOutreaches.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to fetch campaigns');
    });

    // Fetch single outreach
    builder.addCase(fetchOutreach.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchOutreach.fulfilled, (state, { payload }: any) => {
      state.loading = false;
      state.selectedOutreach = payload?.data || payload;
    });
    builder.addCase(fetchOutreach.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to fetch campaign details');
    });

    // Cancel outreach
    builder.addCase(cancelOutreach.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cancelOutreach.fulfilled, (state) => {
      state.loading = false;
      toast.success('Campaign cancelled');
    });
    builder.addCase(cancelOutreach.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to cancel campaign');
    });

    // Retry outreach
    builder.addCase(retryOutreach.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(retryOutreach.fulfilled, (state) => {
      state.loading = false;
      toast.success('Retry queued for failed recipients');
    });
    builder.addCase(retryOutreach.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to retry campaign');
    });
  },
});

export const uploadOutreach = createAsyncThunk(
  'outreach/upload',
  async (formData: FormData) => {
    const data = await outreachApi.createOutreach(formData);
    return data;
  },
);

export const sendOutreach = createAsyncThunk(
  'outreach/send',
  async (params: { id: number; cb?: () => void }) => {
    const { id, cb } = params;
    const data = await outreachApi.sendOutreach(id);
    cb?.();
    return data;
  },
);

export const fetchOutreaches = createAsyncThunk(
  'outreach/fetchAll',
  async (params?: { page?: number; perPage?: number }) => {
    const data = await outreachApi.listOutreaches(params?.page, params?.perPage);
    return data;
  },
);

export const fetchOutreach = createAsyncThunk(
  'outreach/fetchOne',
  async (params: { id: number; recipientPage?: number; recipientPerPage?: number }) => {
    const data = await outreachApi.getOutreach(params.id, params.recipientPage, params.recipientPerPage);
    return data;
  },
);

export const cancelOutreach = createAsyncThunk(
  'outreach/cancel',
  async (params: { id: number; cb?: () => void }) => {
    const { id, cb } = params;
    const data = await outreachApi.cancelOutreach(id);
    cb?.();
    return data;
  },
);

export const retryOutreach = createAsyncThunk(
  'outreach/retry',
  async (params: { id: number; cb?: () => void }) => {
    const { id, cb } = params;
    const data = await outreachApi.retryOutreach(id);
    cb?.();
    return data;
  },
);

export const outreachSelector = (state: RootState) => state.outreach;
export const { clearCreateResult, clearSelectedOutreach } = outreachSlice.actions;
export default outreachSlice.reducer;
